// Phase SA-4/5 — Brief §03 A4: Bookmaker performance.
//
// 4-col grid (5-col for Pro) showing per-bookmaker stats:
//   Bookmaker | Legs | Win · Process | CLV (Pro)
//
// Reads from user_betting_profile.bookmaker_performance (JSONB):
//   { '<bookmaker_slug>': { legs, wins, rate, process_accuracy, clv_pct? } }
//
// Rules (brief verbatim):
//   • Sorted by legs desc (most-used bookmaker first).
//   • Min 3 legs per bookmaker before showing rate → 'Insufficient data'.
//   • CLV column is Pro-only — Free users see header lock chip
//     + cells blurred (same pattern as MarketPerformanceMatrix).
//   • Empty state: "Bookmaker breakdown appears after your first
//     autopsy. Include the bookmaker when submitting."
//   • Two-sentence insight: "You submit most slips from {top}.
//     Your process accuracy is highest on {bestProcess}."

import type { BookmakerEntry } from "../../slips/useBettingProfile.ts";

const MIN_LEGS_PER_BOOKMAKER = 3;

const BOOKMAKER_LABELS: Record<string, string> = {
  stake: "Stake",
  betway: "Betway",
  sportybet: "Sportybet",
  "1xbet": "1xBet",
  bet9ja: "Bet9ja",
  msport: "MSport",
  other: "Other",
};

function bookmakerLabel(slug: string): string {
  return BOOKMAKER_LABELS[slug.toLowerCase()] ?? slug;
}

function pct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${Math.round(n * 100)}%`;
}

function clvLabel(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const sign = n >= 0 ? "+" : "";
  return `${sign}${n.toFixed(1)}%`;
}

export interface BookmakerPerformanceProps {
  data: Record<string, BookmakerEntry>;
  pro?: boolean;
}

/** Pure derivation — sort by legs desc. Returns entries with their
 * slug attached for stable React keys. */
export function sortBookmakers(
  data: Record<string, BookmakerEntry>,
): Array<{ slug: string; row: BookmakerEntry }> {
  return Object.entries(data)
    .map(([slug, row]) => ({ slug, row }))
    .filter((e) => e.row && (e.row.legs ?? 0) > 0)
    .sort((a, b) => (b.row.legs ?? 0) - (a.row.legs ?? 0));
}

/** Pure derivation — top bookmaker by legs (most-used). */
export function topByLegs(
  data: Record<string, BookmakerEntry>,
): { slug: string; label: string; legs: number } | null {
  const sorted = sortBookmakers(data);
  if (sorted.length === 0) return null;
  const top = sorted[0]!;
  return { slug: top.slug, label: bookmakerLabel(top.slug), legs: top.row.legs };
}

/** Pure derivation — bookmaker with the highest process accuracy
 * (among those with >= MIN_LEGS_PER_BOOKMAKER legs). */
export function topByProcess(
  data: Record<string, BookmakerEntry>,
): { slug: string; label: string; processAccuracy: number } | null {
  let best: { slug: string; label: string; processAccuracy: number } | null =
    null;
  for (const [slug, row] of Object.entries(data)) {
    if (!row || row.legs < MIN_LEGS_PER_BOOKMAKER) continue;
    if (row.process_accuracy == null || !Number.isFinite(row.process_accuracy))
      continue;
    if (!best || row.process_accuracy > best.processAccuracy) {
      best = {
        slug,
        label: bookmakerLabel(slug),
        processAccuracy: row.process_accuracy,
      };
    }
  }
  return best;
}

export function BookmakerPerformance({
  data,
  pro = false,
}: BookmakerPerformanceProps): JSX.Element {
  const rows = sortBookmakers(data);
  const top = topByLegs(data);
  const best = topByProcess(data);

  if (rows.length === 0) {
    return (
      <div
        data-testid="sa-bookmaker-empty"
        style={{
          padding: "14px 16px",
          border: "1px dashed var(--sa-hairline)",
          borderRadius: 4,
          background: "var(--sa-bg-raised)",
          fontFamily: "var(--sa-font-mono)",
          fontSize: 12,
          color: "var(--sa-fg-secondary)",
          lineHeight: 1.6,
        }}
      >
        Bookmaker breakdown appears after your first autopsy. Include the
        bookmaker when submitting.
      </div>
    );
  }

  return (
    <div data-testid="sa-bookmaker-performance">
      <div
        className="sa-market-matrix"
        data-pro={pro || undefined}
        role="table"
        aria-label="Bookmaker performance"
        style={{
          gridTemplateColumns: "1.4fr 0.7fr 1.2fr 1.2fr",
        }}
      >
        <div className="sa-mm-head" role="columnheader">
          Bookmaker
        </div>
        <div className="sa-mm-head" role="columnheader">
          Legs
        </div>
        <div className="sa-mm-head" role="columnheader">
          Win · Process
        </div>
        <div className="sa-mm-head sa-mm-pro-col" role="columnheader">
          CLV
          {!pro ? (
            <span
              data-testid="sa-bp-pro-lock"
              style={{
                marginLeft: 6,
                padding: "1px 6px",
                border: "1px solid var(--sa-hairline-accent)",
                borderRadius: 999,
                color: "var(--sa-accent-saffron)",
                fontSize: 9,
                letterSpacing: "0.18em",
              }}
              title="Unlock with SIKA Pro"
            >
              🔒 PRO
            </span>
          ) : null}
        </div>

        {rows.map(({ slug, row }) => (
          <BookmakerRow
            key={slug}
            slug={slug}
            row={row}
            pro={pro}
            isTop={top?.slug === slug}
          />
        ))}
      </div>

      <p
        data-testid="sa-bp-insight"
        style={{
          marginTop: 12,
          fontFamily: "var(--sa-font-body)",
          fontSize: 13.5,
          color: "var(--sa-fg-secondary)",
          lineHeight: 1.55,
        }}
      >
        {top ? (
          <span data-testid="sa-bp-insight-top">
            {`You submit most slips from ${top.label}. `}
          </span>
        ) : null}
        {best ? (
          <span data-testid="sa-bp-insight-best">
            {`Your process accuracy is highest on ${best.label} submissions (${Math.round(best.processAccuracy * 100)}%).`}
          </span>
        ) : null}
      </p>
    </div>
  );
}

function BookmakerRow({
  slug,
  row,
  pro,
  isTop,
}: {
  slug: string;
  row: BookmakerEntry;
  pro: boolean;
  isTop: boolean;
}): JSX.Element {
  const insufficient = row.legs < MIN_LEGS_PER_BOOKMAKER;
  const label = bookmakerLabel(slug);

  return (
    <>
      <div
        className="sa-mm-cell"
        data-testid={`sa-bp-${slug}-label`}
        role="cell"
        style={{
          fontWeight: 500,
          color: isTop ? "var(--sa-accent-saffron)" : undefined,
        }}
      >
        {label}
        {isTop ? (
          <span
            style={{
              marginLeft: 8,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 9,
              letterSpacing: "0.16em",
              color: "var(--sa-fg-muted)",
            }}
          >
            MOST USED
          </span>
        ) : null}
      </div>
      <div
        className="sa-mm-cell"
        data-testid={`sa-bp-${slug}-legs`}
        role="cell"
      >
        {row.legs}
      </div>
      {insufficient ? (
        <div
          className="sa-mm-cell"
          data-testid={`sa-bp-${slug}-rate`}
          role="cell"
          data-empty="true"
        >
          Insufficient data
        </div>
      ) : (
        <div
          className="sa-mm-cell"
          data-testid={`sa-bp-${slug}-rate`}
          role="cell"
        >
          <span style={{ fontFamily: "var(--sa-font-stat)", fontSize: 17 }}>
            {pct(row.rate)}
          </span>
          <span style={{ color: "var(--sa-fg-muted)", marginLeft: 6 }}>
            {`· ${pct(row.process_accuracy)}`}
          </span>
        </div>
      )}
      <div
        className="sa-mm-cell sa-mm-pro-col"
        data-testid={`sa-bp-${slug}-clv`}
        role="cell"
        data-empty={row.clv_pct == null || insufficient ? "true" : undefined}
        style={!pro ? { filter: "blur(4px)", userSelect: "none" } : undefined}
        aria-hidden={!pro ? "true" : undefined}
      >
        {insufficient ? "—" : clvLabel(row.clv_pct)}
      </div>
    </>
  );
}
